import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { getDateString } from './firebaseService';

export interface Completion {
  habits: string[];
  freezes?: string[];
  updatedAt: any;
}

// Habit Completion Services
export const getCompletionsForDate = async (userId: string, date: string): Promise<string[]> => {
  try {
    const completionsRef = doc(db, 'users', userId, 'completions', date);
    const completionsDoc = await getDoc(completionsRef);
    
    if (completionsDoc.exists()) {
      return completionsDoc.data().habits || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching completions:', error);
    return [];
  }
};

export const calculateStreak = async (habitId: string, userId: string): Promise<{ current: number; best: number }> => {
  try {
    let currentStreak = 0;
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    // Batch-load last 365 days in parallel
    const promises = [];
    for (let i = 0; i < 365; i++) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      const ds = getDateString(d);
      promises.push(
        getDoc(doc(db, 'users', userId, 'completions', ds))
          .then(snap => ({ ds, data: snap.exists() ? snap.data() : null }))
      );
    }
    const docs = await Promise.all(promises);
    const dataMap: Record<string, any> = {};
    docs.forEach(({ ds, data }) => { dataMap[ds] = data; });

    // Walk backwards counting completed OR frozen days
    const checkDate = new Date(now);
    for (let i = 0; i < 365; i++) {
      const ds = getDateString(checkDate);
      const dayData = dataMap[ds];
      const completed = dayData && (dayData.habits || []).includes(habitId);
      const frozen = dayData && (dayData.freezes || []).includes(habitId);

      if (completed || frozen) {
        currentStreak++;
        checkDate.setDate(checkDate.getDate() - 1);
      } else {
        break;
      }
    }

    const habitDoc = await getDoc(doc(db, 'habits', habitId));
    const existingBest = habitDoc.exists() ? habitDoc.data()?.bestStreak || 0 : 0;

    return {
      current: currentStreak,
      best: Math.max(currentStreak, existingBest)
    };
  } catch (error) {
    console.error('Error calculating streak:', error);
    return { current: 0, best: 0 };
  }
};

export const toggleHabitCompletion = async (habitId: string, userId: string, targetDate?: Date): Promise<boolean> => {
  try {
    const date = targetDate || new Date();
    const dateString = getDateString(date);
    
    // Get/create completions document for the date
    const completionsRef = doc(db, 'users', userId, 'completions', dateString);
    const completionsDoc = await getDoc(completionsRef);
    let currentCompletions = completionsDoc.exists() ? 
      (completionsDoc.data().habits || []) : [];
    
    // Toggle completion
    const isCompleted = currentCompletions.includes(habitId);
    if (isCompleted) {
      currentCompletions = currentCompletions.filter((id: string) => id !== habitId);
    } else {
      currentCompletions = Array.from(new Set([...currentCompletions, habitId]));
    }
    
    // Update document or delete if empty (preserve freezes)
    const existingData = completionsDoc.exists() ? completionsDoc.data() : {};
    const hasFreezes = (existingData.freezes || []).length > 0;

    if (currentCompletions.length > 0 || hasFreezes) {
      await setDoc(completionsRef, {
        ...existingData,
        habits: currentCompletions,
        updatedAt: serverTimestamp()
      });
    } else {
      await deleteDoc(completionsRef);
    }
    
    // Defer streak calculation (non-blocking — UI updates via listener)
    calculateStreak(habitId, userId).then(({ current, best }) => {
      updateDoc(doc(db, 'habits', habitId), {
        streak: current,
        bestStreak: best,
        lastCompleted: !isCompleted ? dateString : null,
        updatedAt: serverTimestamp()
      }).catch(() => {});
    }).catch(() => {});

    return !isCompleted; // Return new completion state immediately
  } catch (error) {
    console.error('Error toggling habit completion:', error);
    throw error;
  }
};

export const getHabitCompletionsForDateRange = async (
  userId: string,
  startDate: Date,
  endDate: Date
): Promise<Record<string, { habits: string[]; freezes: string[] }>> => {
  try {
    // Batch read all days in parallel
    const dates: string[] = [];
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      dates.push(getDateString(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    const results = await Promise.all(
      dates.map(ds =>
        getDoc(doc(db, 'users', userId, 'completions', ds))
          .then(snap => ({ ds, data: snap.exists() ? snap.data() : null }))
      )
    );

    const completions: Record<string, { habits: string[]; freezes: string[] }> = {};
    results.forEach(({ ds, data }) => {
      if (data) {
        completions[ds] = {
          habits: data.habits || [],
          freezes: data.freezes || [],
        };
      }
    });

    return completions;
  } catch (error) {
    console.error('Error fetching completion range:', error);
    return {};
  }
};

export const getWeeklyHabitProgress = async (habitId: string, userId: string): Promise<boolean[]> => {
  try {
    const progress: boolean[] = [];
    const today = new Date();
    
    // Get last 7 days (Monday to Sunday)
    for (let i = 6; i >= 0; i--) {
      const checkDate = new Date(today);
      checkDate.setDate(today.getDate() - i);
      const dateString = getDateString(checkDate);
      
      const completions = await getCompletionsForDate(userId, dateString);
      progress.push(completions.includes(habitId));
    }
    
    return progress;
  } catch (error) {
    console.error('Error fetching weekly progress:', error);
    return Array(7).fill(false);
  }
};

export const subscribeToUserCompletions = (userId: string, callback: (completions: Record<string, Completion>) => void) => {
  const completionsRef = collection(db, 'users', userId, 'completions');

  const tryQuery = (useOrderBy: boolean) => {
    const q = useOrderBy
      ? query(completionsRef, orderBy('__name__', 'desc'))
      : query(completionsRef);

    return onSnapshot(q, (snapshot) => {
      const completions: Record<string, Completion> = {};
      snapshot.docs.forEach(doc => {
        completions[doc.id] = doc.data() as Completion;
      });
      callback(completions);
    }, (error) => {
      console.error('Error fetching completions:', error);
      if (useOrderBy) {
        console.log('Retrying completions query without orderBy...');
        unsub = tryQuery(false);
      }
    });
  };

  let unsub = tryQuery(true);
  return () => unsub();
};

export const getHabitStats = async (habitId: string, userId: string) => {
  try {
    const { current, best } = await calculateStreak(habitId, userId);
    const weeklyProgress = await getWeeklyHabitProgress(habitId, userId);
    const completedThisWeek = weeklyProgress.filter(completed => completed).length;
    const weeklyPercentage = Math.round((completedThisWeek / 7) * 100);

    // Get total maintained days (completed + frozen) in last 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const monthlyCompletions = await getHabitCompletionsForDateRange(userId, thirtyDaysAgo, new Date());

    let monthlyTotal = 0;
    Object.values(monthlyCompletions).forEach(dayData => {
      const habits = dayData.habits || [];
      const freezes = dayData.freezes || [];
      if (habits.includes(habitId) || freezes.includes(habitId)) {
        monthlyTotal++;
      }
    });
    
    return {
      currentStreak: current,
      bestStreak: best,
      weeklyProgress,
      completedThisWeek,
      weeklyPercentage,
      monthlyTotal,
      monthlyPercentage: Math.round((monthlyTotal / 30) * 100)
    };
  } catch (error) {
    console.error('Error fetching habit stats:', error);
    return {
      currentStreak: 0,
      bestStreak: 0,
      weeklyProgress: Array(7).fill(false),
      completedThisWeek: 0,
      weeklyPercentage: 0,
      monthlyTotal: 0,
      monthlyPercentage: 0
    };
  }
};

// Check if a habit has used its weekly freeze (Mon-Sun)
export const checkWeeklyFreezeUsed = async (habitId: string, userId: string): Promise<boolean> => {
  const now = new Date();
  const day = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
  monday.setHours(0, 0, 0, 0);

  const promises = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    if (d > now) break;
    const ds = getDateString(d);
    promises.push(
      getDoc(doc(db, 'users', userId, 'completions', ds))
        .then(snap => snap.exists() && (snap.data().freezes || []).includes(habitId))
    );
  }
  const results = await Promise.all(promises);
  return results.some(Boolean);
};

// Use streak freeze for a specific missed date
export const useStreakFreeze = async (habitId: string, userId: string, missedDate: Date): Promise<void> => {
  const dateString = getDateString(missedDate);
  const completionsRef = doc(db, 'users', userId, 'completions', dateString);

  const snap = await getDoc(completionsRef);
  const existing = snap.exists() ? snap.data() : {};
  const currentFreezes = existing.freezes || [];
  const currentHabits = existing.habits || [];

  if (currentFreezes.includes(habitId)) throw new Error('Already frozen for this date');
  if (currentHabits.includes(habitId)) throw new Error('Already completed for this date');

  const alreadyUsed = await checkWeeklyFreezeUsed(habitId, userId);
  if (alreadyUsed) throw new Error('Weekly freeze already used for this habit');

  await setDoc(completionsRef, {
    habits: currentHabits,
    freezes: [...currentFreezes, habitId],
    updatedAt: serverTimestamp()
  }, { merge: true });

  const { current, best } = await calculateStreak(habitId, userId);
  await updateDoc(doc(db, 'habits', habitId), {
    streak: current,
    bestStreak: best,
    updatedAt: serverTimestamp()
  });
};

// Undo a streak freeze
export const undoStreakFreeze = async (habitId: string, userId: string, frozenDate: Date): Promise<void> => {
  const dateString = getDateString(frozenDate);
  const completionsRef = doc(db, 'users', userId, 'completions', dateString);

  const snap = await getDoc(completionsRef);
  if (!snap.exists()) return;

  const data = snap.data();
  const updatedFreezes = (data.freezes || []).filter((id: string) => id !== habitId);

  if (updatedFreezes.length === 0 && (!data.habits || data.habits.length === 0)) {
    await deleteDoc(completionsRef);
  } else {
    await updateDoc(completionsRef, { freezes: updatedFreezes, updatedAt: serverTimestamp() });
  }

  const { current, best } = await calculateStreak(habitId, userId);
  await updateDoc(doc(db, 'habits', habitId), {
    streak: current,
    bestStreak: best,
    updatedAt: serverTimestamp()
  });
};

// Delete habit and clean up all completion references
export const deleteHabitAndCleanup = async (habitId: string, userId: string): Promise<void> => {
  await deleteDoc(doc(db, 'habits', habitId));

  // Remove habitId from all completion docs
  const completionsRef = collection(db, 'users', userId, 'completions');
  const snapshot = await getDocs(completionsRef);

  const updates: Promise<void>[] = [];
  snapshot.forEach(docSnap => {
    const data = docSnap.data();
    const habits = (data.habits || []).filter((id: string) => id !== habitId);
    const freezes = (data.freezes || []).filter((id: string) => id !== habitId);

    if (habits.length === 0 && freezes.length === 0) {
      updates.push(deleteDoc(docSnap.ref));
    } else {
      updates.push(updateDoc(docSnap.ref, { habits, freezes }));
    }
  });

  await Promise.all(updates);
};

const habitService = {
  getCompletionsForDate,
  calculateStreak,
  toggleHabitCompletion,
  getHabitCompletionsForDateRange,
  getWeeklyHabitProgress,
  subscribeToUserCompletions,
  getHabitStats,
  checkWeeklyFreezeUsed,
  useStreakFreeze,
  undoStreakFreeze,
  deleteHabitAndCleanup
};

export default habitService;